/**
 * Reading a catalogue, without React.
 *
 * Separate from `index.tsx` so that anything producing text outside a component
 * — and the tests that check the catalogues — can translate without mounting a
 * provider.
 */

import { LOCALES, type Locale } from '@kanto/shared';
import { en, type Messages } from './messages-en';
import { fr } from './messages-fr';

export type { Messages } from './messages-en';

export type MessageKey = keyof Messages;

/**
 * The arguments a message expects: none for a fixed sentence, those of the
 * function for a message that names or counts something. Read off the English
 * catalogue, which is what makes `t('home.greeting')` without its username a
 * compile error rather than the word `undefined` on screen.
 */
type Params<K extends MessageKey> = Messages[K] extends (...args: infer A) => string ? A : [];

/**
 * The translation function, which **also carries its language**.
 *
 * `t.locale` is there so that anything speaking to a person takes one parameter
 * rather than two — a date formatter needs the language without needing a
 * message, and threading a second argument through every call site eventually
 * sees one of them passed the wrong way round.
 */
export type Translate = (<K extends MessageKey>(key: K, ...args: Params<K>) => string) & {
  locale: Locale;
};

const CATALOGUES: Record<Locale, Messages> = { en, fr };

/**
 * Language names, written in the language they name.
 *
 * Not in the catalogues: "Français" is spelt the same on an English screen, and
 * translating it would show "French" to somebody looking for their own language
 * in a list they cannot read.
 */
export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', fr: 'Français' };

/** Supported languages, in the order the switcher offers them. */
export const AVAILABLE_LOCALES = LOCALES;

/** The translation function for one language. */
export function makeTranslate(locale: Locale): Translate {
  const catalogue = CATALOGUES[locale];
  const translate = <K extends MessageKey>(key: K, ...args: Params<K>): string => {
    const message = catalogue[key];
    // The catalogue is typed, so a message is either a sentence or a function of
    // its parameters; the cast only tells the compiler which one this key is.
    return typeof message === 'function'
      ? (message as (...params: unknown[]) => string)(...args)
      : message;
  };
  return Object.assign(translate, { locale });
}
